import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useTenant } from "@/hooks/useTenant";
import { fetchAreaRows } from "@/lib/areasApi";
import { catalogAreasForTenant, orderByTenantList, rowToArea, type Area } from "@/lib/areas";

export const areasQueryKey = (tenantId: string) => ["areas", tenantId] as const;
export const adminAreasQueryKey = (tenantId: string) => ["areas-admin", tenantId] as const;

export type AreasSource = "db" | "catalog";

/**
 * The current city's areas: database rows when the table has any for this
 * city, else the code catalog. Only slugs in the city's Locations list are
 * shown, in that order, so Tenants → Locations stays the one membership list.
 */
export function useAreas(): {
  areas: Area[];
  bySlug: Map<string, Area>;
  isLoading: boolean;
  isError: boolean;
  source: AreasSource;
} {
  const tenant = useTenant();
  const query = useQuery({
    queryKey: areasQueryKey(tenant.id),
    queryFn: () => fetchAreaRows(tenant.id),
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const center = useMemo<[number, number] | null>(
    () => (tenant.geoLat != null && tenant.geoLng != null ? [tenant.geoLng, tenant.geoLat] : null),
    [tenant.geoLat, tenant.geoLng],
  );

  const { areas, source } = useMemo(() => {
    const rows = query.data;
    const allowed = new Set(tenant.areaSlugs);
    if (rows && rows.length > 0) {
      const list = rows.filter((r) => allowed.has(r.slug)).map((r) => rowToArea(r, center));
      return { areas: orderByTenantList(list, tenant.areaSlugs), source: "db" as const };
    }
    return { areas: catalogAreasForTenant(tenant), source: "catalog" as const };
  }, [query.data, tenant, center]);

  const bySlug = useMemo(() => new Map(areas.map((a) => [a.slug, a])), [areas]);

  return { areas, bySlug, isLoading: query.isLoading, isError: query.isError, source };
}

export function useArea(slug: string | undefined): { area: Area | null; isLoading: boolean; source: AreasSource } {
  const { bySlug, isLoading, source } = useAreas();
  return { area: slug ? bySlug.get(slug) ?? null : null, isLoading, source };
}

/** Refetch the current city's areas after an admin edit. */
export function useInvalidateAreas(): () => void {
  const tenant = useTenant();
  const qc = useQueryClient();
  return useCallback(() => {
    qc.invalidateQueries({ queryKey: areasQueryKey(tenant.id) });
    qc.invalidateQueries({ queryKey: adminAreasQueryKey(tenant.id) });
  }, [qc, tenant.id]);
}
